<template>
  <div class="dashboard">
    <div class="dashboard-container">
      <div class="dashboard-header">
        <h1>User Dashboard</h1>
        <div class="header-actions">
          <div class="search-box">
            <input 
              type="text" 
              v-model="searchQuery" 
              placeholder="Search by name, user ID, device..."
              @input="filterUsers"
            >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </div>
          <button @click="exportUserIds" class="export-btn">Export User IDs</button>
          <button @click="logout" class="logout-btn">Logout</button>
        </div>
      </div>

      <div class="stats-container">
        <div class="stats-card">
          <h3>Daily New Users</h3>
          <LineChart 
            :data="dailyStats.newUsers" 
            title="New Users"
            color="#128078"
          />
        </div>
        <div class="stats-card">
          <h3>Daily New Scans</h3>
          <LineChart 
            :data="dailyStats.newScans" 
            title="New Scans"
            color="#16c79a"
          />
        </div>
      </div>

      <div class="users-table-container">
        <div v-if="loading" class="loading-state">
          <div class="cosmic-loader"></div>
          <span>Loading users...</span>
        </div>
        
        <div v-else class="table-scroll-wrapper">
          <table class="users-table">
            <thead>
              <tr>
                <th @click="sortBy('name')" class="sortable">Name <span class="sort-arrow" v-if="sortKey === 'name'">{{ sortDirection === 1 ? '▲' : '▼' }}</span></th>
                <th @click="sortBy('model')" class="sortable">Device <span class="sort-arrow" v-if="sortKey === 'model'">{{ sortDirection === 1 ? '▲' : '▼' }}</span></th>
                <th @click="sortBy('systemVersion')" class="sortable">OS Version <span class="sort-arrow" v-if="sortKey === 'systemVersion'">{{ sortDirection === 1 ? '▲' : '▼' }}</span></th>
                <th @click="sortBy('language')" class="sortable">Language <span class="sort-arrow" v-if="sortKey === 'language'">{{ sortDirection === 1 ? '▲' : '▼' }}</span></th>
                <th @click="sortBy('timeZone')" class="sortable">Time Zone <span class="sort-arrow" v-if="sortKey === 'timeZone'">{{ sortDirection === 1 ? '▲' : '▼' }}</span></th>
                <th @click="sortBy('totalScans')" class="sortable">Total Scans <span class="sort-arrow" v-if="sortKey === 'totalScans'">{{ sortDirection === 1 ? '▲' : '▼' }}</span></th>
                <th @click="sortBy('lastScanDate')" class="sortable">Last Scan <span class="sort-arrow" v-if="sortKey === 'lastScanDate'">{{ sortDirection === 1 ? '▲' : '▼' }}</span></th>
                <th @click="sortBy('createdAt')" class="sortable">Created At <span class="sort-arrow" v-if="sortKey === 'createdAt'">{{ sortDirection === 1 ? '▲' : '▼' }}</span></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="user in sortedUsers" :key="user.uid" class="user-row" @click="viewUserDetail(user.uid)">
                <td>
                  <div class="user-info">
                    <div class="user-avatar">{{ getUserInitials(user.name) }}</div>
                    <span>{{ formatName(user.name) }}</span>
                  </div>
                </td>
                <td>{{ formatDevice(user.model) }}</td>
                <td>{{ formatSystemVersion(user.systemVersion) }}</td>
                <td>{{ formatLanguage(user.language) }}</td>
                <td>{{ formatTimeZone(user.timeZone) }}</td>
                <td>{{ user.totalScans || 0 }}</td>
                <td>{{ formatLastScanDate(user.lastScanDate) }}</td>
                <td>{{ formatCreatedAt(user.createdAt) }}</td>
              </tr>
              <tr v-if="sortedUsers.length === 0">
                <td colspan="8" class="no-data">No users found</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { FirebaseUserService } from '../services/firebaseUserService'
import LineChart from './LineChart.vue'

export default {
  name: 'AdminDashboard',
  components: {
    LineChart
  },
  setup() {
    const router = useRouter()
    const userService = new FirebaseUserService()
    const users = ref([])
    const filteredUsers = ref([])
    const allScans = ref([])
    const searchQuery = ref('')
    const loading = ref(true)
    const sortKey = ref('createdAt')
    const sortDirection = ref(-1)
    const dailyStats = ref({
      newUsers: [],
      newScans: []
    })

    const loadUsers = async () => {
      try {
        loading.value = true
        const result = await userService.getAllUsers()
        users.value = result.users
        allScans.value = result.allScans
        filteredUsers.value = result.users
        
        // Calculate daily stats from existing data
        const stats = userService.calculateDailyStats(result.users, result.allScans, 7)
        dailyStats.value = stats
      } catch (error) {
        console.error('Error loading users:', error)
      } finally {
        loading.value = false
      }
    }

    const filterUsers = () => {
      if (!searchQuery.value.trim()) {
        filteredUsers.value = users.value
        return
      }

      const query = searchQuery.value.toLowerCase()
      filteredUsers.value = users.value.filter(user => 
        user.name?.toLowerCase().includes(query) ||
        user.uid?.toLowerCase().includes(query) ||
        user.model?.toLowerCase().includes(query) ||
        user.language?.toLowerCase().includes(query) ||
        user.timeZone?.toLowerCase().includes(query)
      )
    }

    const sortBy = (key) => {
      if (sortKey.value === key) {
        sortDirection.value = sortDirection.value === 1 ? -1 : 1
      } else {
        sortKey.value = key
        sortDirection.value = 1
      }
    }

    const sortedUsers = computed(() => {
      const sorted = [...filteredUsers.value].sort((a, b) => {
        let aVal, bVal

        switch (sortKey.value) {
          case 'name':
            aVal = a.name || ''
            bVal = b.name || ''
            break
          case 'model':
            aVal = a.model || ''
            bVal = b.model || ''
            break
          case 'systemVersion':
            aVal = a.systemVersion || ''
            bVal = b.systemVersion || ''
            break
          case 'language':
            aVal = a.language || ''
            bVal = b.language || ''
            break
          case 'timeZone':
            aVal = a.timeZone || ''
            bVal = b.timeZone || ''
            break
          case 'totalScans':
            aVal = a.totalScans || 0
            bVal = b.totalScans || 0
            break
          case 'lastScanDate':
            aVal = a.lastScanDate?.toDate?.() || new Date(0)
            bVal = b.lastScanDate?.toDate?.() || new Date(0)
            break
          case 'createdAt':
            aVal = a.createdAt?.toDate?.() || new Date(0)
            bVal = b.createdAt?.toDate?.() || new Date(0)
            break
          default:
            aVal = a[sortKey.value] || ''
            bVal = b[sortKey.value] || ''
        }

        if (aVal < bVal) return sortDirection.value === 1 ? -1 : 1
        if (aVal > bVal) return sortDirection.value === 1 ? 1 : -1
        return 0
      })

      return sorted
    })

    const getUserInitials = (name) => {
      if (!name) return '?'
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    }

    const formatName = (name) => {
      return name || 'Unknown'
    }

    const formatDevice = (model) => {
      return model || '-'
    }

    const formatSystemVersion = (version) => {
      return version || '-'
    }

    const formatLanguage = (language) => {
      if (!language) return '-'
      
      const languageMap = {
        'en_TR': 'English (Turkey)',
        'en_US': 'English (US)',
        'en_GB': 'English (UK)',
        'en_IL': 'English (Israel)',
        'en_ZA': 'English (South Africa)',
        'en_CH': 'English (Switzerland)',
        'en_BH': 'English (Bahrain)',
        'en_SE': 'English (Sweden)',
        'en_C2': 'English (Custom)',
        'en_MO': 'English (Macau)',
        'en_EG': 'English (Egypt)',
        'en_IE': 'English (Ireland)',
        'en_CN': 'English (China)',
        'en_SG': 'English (Singapore)',
        'tr_TR': 'Turkish',
        'de_DE': 'German',
        'fr_FR': 'French',
        'es_ES': 'Spanish',
        'it_IT': 'Italian',
        'pt_PT': 'Portuguese',
        'ru_RU': 'Russian',
        'ja_JP': 'Japanese',
        'ko_KR': 'Korean',
        'zh_CN': 'Chinese (Simplified)',
        'zh_TW': 'Chinese (Traditional)',
        'zh_MO': 'Chinese (Macau)',
        'zh_EG': 'Chinese (Egypt)',
        'zh_IE': 'Chinese (Ireland)',
        'zh_SG': 'Chinese (Singapore)',
        'ar_SA': 'Arabic',
        'ar_IL': 'Arabic (Israel)',
        'ar_ZA': 'Arabic (South Africa)',
        'ar_CH': 'Arabic (Switzerland)',
        'ar_BH': 'Arabic (Bahrain)',
        'ar_SE': 'Arabic (Sweden)',
        'ar_MO': 'Arabic (Macau)',
        'ar_EG': 'Arabic (Egypt)',
        'ar_IE': 'Arabic (Ireland)',
        'ar_CN': 'Arabic (China)',
        'ar_SG': 'Arabic (Singapore)',
        'hi_IN': 'Hindi',
        'nl_NL': 'Dutch',
        'sv_SE': 'Swedish',
        'no_NO': 'Norwegian',
        'da_DK': 'Danish',
        'fi_FI': 'Finnish',
        'pl_PL': 'Polish',
        'cs_CZ': 'Czech',
        'en_CZ': 'English (Czech Republic)',
        'ar_CZ': 'Arabic (Czech Republic)',
        'zh_CZ': 'Chinese (Czech Republic)',
        'pt_CZ': 'Portuguese (Czech Republic)',
        'fr_CZ': 'French (Czech Republic)',
        'de_CZ': 'German (Czech Republic)',
        'es_CZ': 'Spanish (Czech Republic)',
        'it_CZ': 'Italian (Czech Republic)',
        'ja_CZ': 'Japanese (Czech Republic)',
        'ko_CZ': 'Korean (Czech Republic)',
        'ru_CZ': 'Russian (Czech Republic)',
        'tr_CZ': 'Turkish (Czech Republic)',
        'hu_HU': 'Hungarian',
        'ro_RO': 'Romanian',
        'bg_BG': 'Bulgarian',
        'hr_HR': 'Croatian',
        'sk_SK': 'Slovak',
        'sl_SI': 'Slovenian',
        'et_EE': 'Estonian',
        'lv_LV': 'Latvian',
        'lt_LT': 'Lithuanian',
        'he_IL': 'Hebrew (Israel)',
        'af_ZA': 'Afrikaans (South Africa)',
        'de_CH': 'German (Switzerland)',
        'fr_CH': 'French (Switzerland)',
        'it_CH': 'Italian (Switzerland)',
        'rm_CH': 'Romansh (Switzerland)',
        'pt_MO': 'Portuguese (Macau)',
        'pt_EG': 'Portuguese (Egypt)',
        'pt_IE': 'Portuguese (Ireland)',
        'pt_CN': 'Portuguese (China)',
        'pt_SG': 'Portuguese (Singapore)',
        'fr_MO': 'French (Macau)',
        'fr_EG': 'French (Egypt)',
        'fr_IE': 'French (Ireland)',
        'fr_CN': 'French (China)',
        'fr_SG': 'French (Singapore)',
        'de_MO': 'German (Macau)',
        'de_EG': 'German (Egypt)',
        'de_IE': 'German (Ireland)',
        'de_CN': 'German (China)',
        'de_SG': 'German (Singapore)',
        'es_MO': 'Spanish (Macau)',
        'es_EG': 'Spanish (Egypt)',
        'es_IE': 'Spanish (Ireland)',
        'es_CN': 'Spanish (China)',
        'es_SG': 'Spanish (Singapore)',
        'ja_MO': 'Japanese (Macau)',
        'ja_EG': 'Japanese (Egypt)',
        'ja_IE': 'Japanese (Ireland)',
        'ja_CN': 'Japanese (China)',
        'ja_SG': 'Japanese (Singapore)',
        'ko_MO': 'Korean (Macau)',
        'ko_EG': 'Korean (Egypt)',
        'ko_IE': 'Korean (Ireland)',
        'ko_CN': 'Korean (China)',
        'ko_SG': 'Korean (Singapore)',
        'ru_MO': 'Russian (Macau)',
        'ru_EG': 'Russian (Egypt)',
        'ru_IE': 'Russian (Ireland)',
        'ru_CN': 'Russian (China)',
        'ru_SG': 'Russian (Singapore)',
        'tr_MO': 'Turkish (Macau)',
        'tr_EG': 'Turkish (Egypt)',
        'tr_IE': 'Turkish (Ireland)',
        'tr_CN': 'Turkish (China)',
        'tr_SG': 'Turkish (Singapore)'
      }
      
      return languageMap[language] || language
    }

    const formatTimeZone = (timeZone) => {
      return timeZone || '-'
    }

    const formatArray = (array) => {
      if (!array || array.length === 0) return '-'
      return array.join(', ')
    }

    const formatCreatedAt = (createdAt) => {
      if (!createdAt) return '-'
      const date = createdAt.toDate ? createdAt.toDate() : new Date(createdAt)
      return date.toLocaleDateString()
    }

    const formatLastScanDate = (lastScanDate) => {
      if (!lastScanDate) return '-'
      const date = lastScanDate.toDate ? lastScanDate.toDate() : new Date(lastScanDate)
      return date.toLocaleDateString()
    }

    const logout = () => {
      document.cookie = 'adminAuth=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;'
      router.push('/admin')
    }

    const viewUserDetail = (userId) => {
      router.push(`/user/${userId}`)
    }

    const exportUserIds = () => {
      const cutoffDate = new Date('2025-09-16');
      const filteredUsers = users.value.filter(user => {
        if (!user.createdAt) return false;
        const userCreationDate = user.createdAt.toDate ? user.createdAt.toDate() : new Date(user.createdAt);
        return userCreationDate > cutoffDate;
      });

      const userIds = filteredUsers.map(user => user.uid)
      const jsonToExport = {
        userIds: userIds
      }
      const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(jsonToExport, null, 2));
      const downloadAnchorNode = document.createElement('a');
      downloadAnchorNode.setAttribute("href", dataStr);
      downloadAnchorNode.setAttribute("download", "user_ids_after_2025-09-16.json");
      document.body.appendChild(downloadAnchorNode); // required for firefox
      downloadAnchorNode.click();
      downloadAnchorNode.remove();
    }

    onMounted(() => {
      loadUsers()
    })

    return {
      users,
      filteredUsers,
      searchQuery,
      loading,
      sortKey,
      sortDirection,
      sortedUsers,
      loadUsers,
      filterUsers,
      sortBy,
      getUserInitials,
      formatName,
      formatDevice,
      formatSystemVersion,
      formatLanguage,
      formatTimeZone,
      formatArray,
      formatCreatedAt,
      formatLastScanDate,
      dailyStats,
      logout,
      viewUserDetail,
      exportUserIds
    }
  }
}
</script>

<style scoped>
.dashboard {
  min-height: 100vh;
  padding: 20px;
  background: linear-gradient(135deg, var(--background-primary) 0%, var(--background-secondary) 100%) !important;
  background-attachment: fixed !important;
}

.dashboard-container {
  max-width: 1400px;
  margin: 0 auto;
  background: rgba(10, 46, 43, 0.95);
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  border: 1px solid var(--border-color);
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  flex-wrap: wrap;
  gap: 20px;
}

.dashboard-header h1 {
  color: var(--text-primary);
  margin: 0;
  background: linear-gradient(135deg, var(--primary-color), var(--accent-color));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.stats-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 32px;
}

.stats-card {
  background: rgba(10, 46, 43, 0.95);
  border-radius: 16px;
  padding: 24px;
  border: 1px solid var(--border-color);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
}

.stats-card h3 {
  color: var(--text-primary);
  margin: 0 0 16px 0;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
}

.dashboard-header h1 {
  font-size: 28px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.search-box {
  position: relative;
  display: flex;
  align-items: center;
}

.search-box input {
  padding: 12px 16px 12px 44px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  color: var(--text-primary);
  font-size: 16px;
  width: 300px;
  transition: all 0.3s ease;
}

.search-box input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(18, 128, 120, 0.2);
}

.search-box svg {
  position: absolute;
  left: 12px;
  color: var(--text-muted);
}

.logout-btn {
  padding: 12px 24px;
  background: var(--error-color);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.3s ease;
}

.logout-btn:hover {
  background-color: var(--error-color);
}

.export-btn {
  background-color: var(--accent-color);
  color: white;
  border: none;
  padding: 10px 18px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 15px;
  transition: background-color 0.3s ease, transform 0.2s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.export-btn:hover {
  background-color: var(--accent-light);
  transform: translateY(-2px);
}



.users-table-container {
  background: rgba(255, 255, 255, 0.02);
  border-radius: 12px;
  padding: 24px;
  border: 1px solid var(--border-color);
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: var(--text-secondary);
}

.cosmic-loader {
  width: 40px;
  height: 40px;
  border: 3px solid var(--border-color);
  border-top: 3px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.table-scroll-wrapper {
  overflow-x: auto;
  border-radius: 8px;
}

.users-table {
  width: 100%;
  border-collapse: collapse;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 8px;
  overflow: hidden;
}

.users-table th {
  background: rgba(18, 128, 120, 0.1);
  color: var(--text-primary);
  padding: 16px 12px;
  text-align: left;
  font-weight: 600;
  border-bottom: 1px solid var(--border-color);
  cursor: pointer;
  transition: all 0.3s ease;
}

.users-table th:hover {
  background: rgba(18, 128, 120, 0.2);
}

.users-table th.sortable {
  position: relative;
}

.sort-arrow {
  margin-left: 8px;
  color: var(--primary-color);
}

.users-table td {
  padding: 16px 12px;
  border-bottom: 1px solid var(--border-color);
  color: var(--text-secondary);
}

.user-row {
  transition: all 0.3s ease;
  cursor: pointer;
}

.user-row:hover {
  background: rgba(18, 128, 120, 0.05);
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary-color), var(--accent-color));
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 14px;
}

.no-data {
  text-align: center;
  color: var(--text-muted);
  padding: 40px;
}

@media (max-width: 768px) {
  .dashboard-header {
    flex-direction: column;
    align-items: stretch;
  }
  
  .header-actions {
    justify-content: center;
  }
  
  .stats-container {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  .stats-card {
    padding: 16px;
  }
  
  .search-box input {
    width: 100%;
    max-width: 300px;
  }
  
  .users-table {
    font-size: 14px;
  }
  
  .users-table th,
  .users-table td {
    padding: 12px 8px;
  }
}
</style> 